import React from "react";
import {
  FileText,
  Hash,
  MessageSquare,
  Search as SearchIcon,
  Users,
  UserRound,
} from "lucide-react";

import EntityAvatar from "components/Entities/EntityAvatar";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import type { Actor } from "types/actor";
import type { SearchResult } from "types/search";
import type { EntityType, Issue } from "util/constants";
import type { SearchLocale } from "./searchCopy";
import { getSearchCopy } from "./searchCopy";
import SearchHighlightedText from "./SearchHighlightedText";

/**
 * Build an `Actor` from an actor-type search result row. Returns
 * `null` when the row is missing the minimum fields needed to render
 * a real avatar (numeric id + slug); the caller falls back to a
 * generic icon in that case.
 */
const buildSearchActor = (result: SearchResult): Actor | null => {
  if (result.type !== "actor") {
    return null;
  }
  const id = typeof result.entityId === "number" ? result.entityId : null;
  const slug =
    result.subtype === "team"
      ? typeof result.metadata.teamname === "string"
        ? result.metadata.teamname
        : ""
      : typeof result.metadata.username === "string"
        ? result.metadata.username
        : "";
  if (id === null || id <= 0 || !slug.trim()) {
    return null;
  }
  const avatarKey =
    typeof result.metadata.avatarKey === "string"
      ? result.metadata.avatarKey
      : "";
  const displayName =
    typeof result.metadata.displayName === "string"
      ? result.metadata.displayName
      : null;
  return {
    id,
    kind: result.subtype === "team" ? "team" : "user",
    username: slug.trim(),
    displayName,
    avatarKey: avatarKey.trim().length > 0 ? avatarKey.trim() : null,
  };
};

interface Props {
  result: SearchResult;
  locale: SearchLocale;
  active?: boolean;
  compact?: boolean;
  onSelect: (result: SearchResult) => void;
}

const getBadgeLabel = (result: SearchResult, locale: SearchLocale): string => {
  if (result.type === "issue") {
    return getSearchCopy(locale, "issue");
  }

  if (result.type === "comment") {
    return getSearchCopy(locale, "comment");
  }

  if (result.type === "actor") {
    return result.subtype === "team"
      ? getSearchCopy(locale, "team")
      : getSearchCopy(locale, "user");
  }

  if (result.type === "tag") {
    return getSearchCopy(locale, "tag");
  }

  return getSearchCopy(locale, "post");
};

const getBadgeClassName = (result: SearchResult): string => {
  if (result.type === "issue") {
    return "bg-soc-purple";
  }

  if (result.type === "comment") {
    return "bg-eoe-text-meta";
  }

  if (result.type === "actor" && result.subtype === "team") {
    return "bg-eoe-entity-team";
  }

  if (result.type === "actor") {
    return "bg-eoe-entity-user";
  }

  if (result.type === "tag") {
    return "bg-eoe-entity-tag";
  }

  return "bg-soc-purple";
};

const getAvatarContent = (
  result: SearchResult,
  imageCdn: string
): { imageSrc: string | null; icon: React.ReactNode } => {
  const avatarKey =
    typeof result.metadata.avatarKey === "string" ? result.metadata.avatarKey : "";

  if (avatarKey) {
    return {
      imageSrc: `${imageCdn}/${avatarKey}`,
      icon: null,
    };
  }

  if (result.type === "actor" && result.subtype === "team") {
    return {
      imageSrc: null,
      icon: <Users className="w-4 h-4" />,
    };
  }

  if (result.type === "actor") {
    return {
      imageSrc: null,
      icon: <UserRound className="w-4 h-4" />,
    };
  }

  if (result.type === "comment") {
    return {
      imageSrc: null,
      icon: <MessageSquare className="w-4 h-4" />,
    };
  }

  if (result.type === "tag") {
    return {
      imageSrc: null,
      icon: <Hash className="w-4 h-4" />,
    };
  }

  if (result.type === "post") {
    return {
      imageSrc: null,
      icon: <FileText className="w-4 h-4" />,
    };
  }

  return {
    imageSrc: null,
    icon: <SearchIcon className="w-4 h-4" />,
  };
};

const SearchResultItem: React.FC<Props> = ({
  result,
  locale,
  active = false,
  compact = false,
  onSelect,
}) => {
  const imageCdn = process.env.REACT_APP_IMAGE_CDN || "";
  const badgeLabel = getBadgeLabel(result, locale);
  const badgeClassName = getBadgeClassName(result);
  const avatar = getAvatarContent(result, imageCdn);

  if (
    result.type === "post" &&
    (result.subtype === "impact" ||
      result.subtype === "teabate" ||
      result.subtype === "solution" ||
      result.subtype === "feedback" ||
      result.subtype === "action")
  ) {
    return (
      <li className={`${compact ? "px-2 py-1" : "border-b border-eoe-border-main dark:border-eoe-dark-border-main last:border-b-0"}`}>
        <EntityCompactCard
          entityId={result.entityId}
          entityType={result.subtype as EntityType}
          showActions={false}
          showVote={false}
          showSubscription={false}
          showLink={false}
          compactCard={true}
          onClick={() => onSelect(result)}
          className="text-xs pr-0"
        />
      </li>
    );
  }

  if (result.type === "issue") {
    const issueEntity = {
      id: result.entityId,
      name: result.title,
      cname: typeof result.metadata.cname === "string" ? result.metadata.cname : result.title,
      properties: {},
      properties_public: {},
      attributes: null,
    } as unknown as Issue;

    return (
      <li className={`${compact ? "" : "border-b border-eoe-border-main dark:border-eoe-dark-border-main last:border-b-0"}`}>
        <div
          className={`px-2 py-1 cursor-pointer ${
            active
              ? "bg-eoe-icon dark:bg-eoe-dark-icon"
              : "hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon"
          }`}
          onClick={() => onSelect(result)}
        >
          <EntityAvatar
            entity={issueEntity}
            entityId={result.entityId}
            entityType="issue"
            entityChip={true}
            showCloseChip={false}
            showLink={false}
            className="text-xs pr-0 bg-transparent"
            customSubtitle={result.subtitle || ""}
          />
        </div>
      </li>
    );
  }

  if (result.type === "actor" && (result.subtype === "user" || result.subtype === "team")) {
    const actor = buildSearchActor(result);
    return (
      <li className={`${compact ? "px-2 py-1" : "border-b border-eoe-border-main dark:border-eoe-dark-border-main last:border-b-0"}`}>
        <div
          className={`flex items-center gap-3 px-2 py-1 ${
            active
              ? "bg-eoe-icon dark:bg-eoe-dark-icon"
              : "hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon"
          }`}
        >
          {/*
           * Composite row: avatar is its own clickable target (story
           * when available, profile otherwise) and the title/subtitle
           * area triggers the row's `onSelect`. Mirrors the Instagram
           * inbox row pattern.
           */}
          <ActorAvatar actor={actor} size={ACTOR_AVATAR_SIZES.header} />
          <button
            type="button"
            onClick={() => onSelect(result)}
            className="min-w-0 flex-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple"
          >
            <div className="truncate text-sm text-eoe-text dark:text-eoe-dark-text">
              <SearchHighlightedText value={result.title} />
            </div>
            {result.subtitle ? (
              <div className="truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <SearchHighlightedText value={result.subtitle} />
              </div>
            ) : null}
          </button>
          <span
            className={`flex flex-shrink-0 items-center justify-center rounded-full px-2 text-xs text-white ${badgeClassName}`}
          >
            {badgeLabel}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li
      className={`group text-eoe-text dark:text-eoe-dark-text ${
        compact ? "" : "border-b border-eoe-border-main dark:border-eoe-dark-border-main last:border-b-0"
      }`}
    >
      <button
        type="button"
        className={`flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left ${
          active
            ? "bg-eoe-icon dark:bg-eoe-dark-icon"
            : "hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon"
        }`}
        onClick={() => onSelect(result)}
      >
        <div className="flex items-start space-x-2 flex-1 min-w-0">
          <span className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-eoe-icon dark:bg-eoe-dark-icon flex items-center justify-center text-eoe-text dark:text-eoe-dark-text">
            {avatar.imageSrc ? (
              <img
                src={avatar.imageSrc}
                alt={result.title}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              avatar.icon
            )}
          </span>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="leading-tight line-clamp-1 text-sm text-eoe-text dark:text-eoe-dark-text">
              <SearchHighlightedText value={result.title} />
            </span>
            {result.subtitle ? (
              <span className="leading-tight line-clamp-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <SearchHighlightedText value={result.subtitle} />
              </span>
            ) : null}
            {!compact && result.snippet ? (
              <span className="mt-1 leading-tight line-clamp-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <SearchHighlightedText value={result.snippet} />
              </span>
            ) : null}
          </div>
        </div>
        <span
          className={`flex flex-shrink-0 rounded-full px-2 text-xs text-white items-center justify-center ${badgeClassName}`}
        >
          {badgeLabel}
        </span>
      </button>
    </li>
  );
};

export default SearchResultItem;
